import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { Project } from "./useDashboardData";

interface UseDashboardRefreshProps {
  recentCustomersData: any[] | undefined;
  projectsData: Project[] | undefined;
}

export function useDashboardRefresh({
  recentCustomersData,
  projectsData,
}: UseDashboardRefreshProps) {
  const queryClient = useQueryClient();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [customerFilter, setCustomerFilter] = useState<"all" | "has-project" | "no-project">("all");

  // Filter recent customers based on project status
  const filteredRecentCustomers = useMemo(() => {
    if (!(recentCustomersData && projectsData)) {
      return [];
    }

    // Map customers with their project counts
    const customersWithProjects = recentCustomersData.map((customer) => {
      // Find all projects for this customer
      const customerProjects = projectsData.filter((project) => {
        const projectCustomer =
          project.customer_name || `${project.first_name || ""} ${project.last_name || ""}`.trim();
        const currentCustomer = `${customer.first_name} ${customer.last_name}`;
        return projectCustomer === currentCustomer;
      });

      return {
        ...customer,
        projectCount: customerProjects.length,
        hasProject: customerProjects.length > 0,
      };
    });

    // Apply filter
    switch (customerFilter) {
      case "has-project":
        return customersWithProjects.filter((c) => c.hasProject);
      case "no-project":
        return customersWithProjects.filter((c) => !c.hasProject);
      default:
        return customersWithProjects;
    }
  }, [recentCustomersData, projectsData, customerFilter]);

  // Refresh functionality
  const handleManualRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["dashboard-projects"] }),
        queryClient.invalidateQueries({ queryKey: ["dashboard-recent-projects"] }),
        queryClient.invalidateQueries({ queryKey: ["dashboard-customers"] }),
        queryClient.invalidateQueries({ queryKey: ["dashboard-all-customers"] }),
        queryClient.invalidateQueries({ queryKey: ["dashboard-recent-customers"] }),
      ]);
      setLastRefresh(new Date());
    } finally {
      setIsRefreshing(false);
    }
  }, [queryClient]);

  // Auto-refresh effect
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      handleManualRefresh();
    }, 30000); // Refresh every 30 seconds

    return () => clearInterval(interval);
  }, [autoRefresh, handleManualRefresh]);

  return {
    isRefreshing,
    autoRefresh,
    lastRefresh,
    customerFilter,
    filteredRecentCustomers,
    setAutoRefresh,
    setCustomerFilter,
    handleManualRefresh,
  };
}
